/**
 * Renderer-internal visual defaults: widths, sizes, opacities and dash patterns.
 * Colours are not here — they come from the resolved chart theme. None of these are part of the
 * public TimeSeriesChart contract; promote one only when a real multi-feature need appears.
 */

export const lineVisuals = {
  defaultWidth: 2,
  highlightedWidth: 3,
  /** Dimmed lines stay readable as context but must not compete with the highlighted one. */
  dimmedWidth: 1,
  defaultOpacity: 1,
  dimmedOpacity: 0.35,
  symbolSize: 6,
  symbolBorderWidth: 1.5,
  /** Dot of a point that a gap cuts off on both sides; a little wider than the line, to stand out. */
  lonePointSize: 5,
  /** A highlighted line is drawn above the others instead of relying on layer order. */
  z: 2,
  highlightedZ: 3,
} as const;

/** Defaults of the hidden value axes the lines are drawn on. */
export const valueAxisVisuals = {
  /**
   * Default margin above and below the data, as a share of its range (see
   * ValueAxisScale.marginRatio); 5% matches the previous client (JFreeChart's default axis margins).
   */
  marginRatio: 0.05,
} as const;

/**
 * A symbol marks a real sample, so a line shows its symbols only while its visible samples are far
 * enough apart to be told apart; the decision is made per line (see getSymbolSeriesIds), so a dense
 * speed graph does not hide the dots of a sparse channel.
 *
 * Symbols are also the most expensive thing a line chart draws: every point becomes its own circle
 * with a stroke and a hit area. Measured with 12 series (SSR, 1400 px): 1200 symbols in total render
 * in ~120 ms, 18 000 in ~900 ms, and the cost follows the total count. The total cap keeps that cost
 * bounded whatever the selection: past it the densest lines drop their symbols first.
 */
export const symbolVisibility = {
  /** Average spacing a line's visible samples need before its symbols are shown. */
  minPixelsPerPoint: 12,
  /** Upper bound on symbols drawn across all lines at once. */
  maxVisibleSymbols: 1_000,
} as const;

/**
 * How many points each line may hand to ECharts. ECharts re-processes every point it holds on each
 * zoom and pan step (sampling included), so the navigation cost follows this budget, not the raw
 * sample count. Measured on a 30-day, 16-series target (~100 000 samples, 4x CPU throttling):
 * raw data kept every wheel step at 100-240 ms of main-thread work.
 */
export const lineDrawingBudget = {
  /**
   * One bucket per this many plot pixels. Each bucket keeps its first, lowest, highest and last
   * point (see selectMinMaxIndices), which draws the line as it would look with every sample.
   */
  pixelsPerBucket: 2,
  /** Floor for very narrow plots, so a phone still shows the shape of the data. */
  minBucketCount: 100,
  /**
   * While zoomed in, the parts of a line outside the visible window are off-screen until the next
   * pan or zoom-out, yet ECharts still processes them on every step. They are therefore drawn from a
   * coarser overview with this share of the buckets; the next settled window replaces them with detail.
   */
  contextBucketRatio: 0.25,
  minContextBucketCount: 25,
  /** The measured width is rounded to this step, so a resize by a few pixels does not re-thin every series. */
  widthStepPx: 100,
  /** Assumed plot width until the container has been measured (and where it cannot be, such as in tests). */
  fallbackPlotWidthPx: 1_200,
} as const;

/**
 * The first render may animate in; later updates must not. Every settled pan or zoom re-thins each
 * line for the new window, so an update animation would replay a 300 ms transition after each
 * navigation gesture and the lines would appear to swim rather than stay put.
 *
 * A zero update duration alone does not stop the work, though: ECharts still diffs the old and new
 * points of every line that may animate, on each zoom and pan step too. Lines that have finished
 * their entry therefore update with animation off (see withoutLineUpdateAnimation).
 */
export const animationVisuals = {
  initialDurationMs: 300,
  updateDurationMs: 0,
} as const;

export const plotVisuals = {
  /** Distance between the time labels and the plot, matching the design's tight spacing. */
  axisLabelMargin: 8,
  /** Horizontal guides behind the lines; a fixed count keeps them even at any zoom level. */
  splitLineCount: 4,
} as const;

export const guideLineVisuals = {
  lineWidth: 1,
  lineType: 'dotted',
  labelPadding: [4, 8],
  /** Negative: pulls the label above the axis line, inside the plot. */
  labelMargin: -30,
} as const;

/** Shared by the plot band of a highlighted lane and the row highlight in the track. */
export const highlightVisuals = {
  fillOpacity: 0x14 / 0xff,
  borderWidth: 1,
  bandBorderDashPattern: [1, 3],
  rowHeightRatio: 0.9,
} as const;

export const rangeBarVisuals = {
  barHeightRatio: 0.5,
  /** A zero-length range still shows as a sliver so it is not lost on the track. */
  minimumBarWidth: 2,
} as const;

export const trackLabelVisuals = {
  markerSize: 8,
  markerGap: 6,
  /** Above every series, so a range bar running under a label never covers it. */
  z: 10,
  /** Matches the chart surface: invisible on an empty lane, a readable plate over a bar. */
  backdropOpacity: 0.9,
  /** Vertical, then horizontal; the horizontal part is taken off the label's offset (see buildTrackLabelOption). */
  backdropPadding: [2, 4],
  backdropBorderRadius: 2,
} as const;

export const sliderVisuals = {
  dataShadowLineWidth: 1,
  dataShadowAreaOpacity: 0.4,
  handleBorderWidth: 1,
} as const;
